import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../contexts/ProposalContext";
import Layout from "../components/layout/Layout";
import ProposalCard from "../components/proposals/ProposalCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";

const ProposalChatPage = () => {
  const {
    proposals,
    loading: proposalsLoading,
    error: proposalsError,
    fetchProposals,
  } = useProposal();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Filter proposals based on search and filters
  const filteredProposals =
    proposals?.filter((proposal) => {
      const matchesSearch =
        !searchTerm ||
        proposal.proposal_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        proposal.customer_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        proposal.project_title
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || proposal.status === statusFilter;

      const matchesType =
        typeFilter === "all" || proposal.proposal_type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    }) || [];

  // Get unique values for filters
  const uniqueStatuses = [...new Set(proposals?.map((p) => p.status) || [])];
  const uniqueTypes = [
    ...new Set(proposals?.map((p) => p.proposal_type) || []),
  ];

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("Active");
    setTypeFilter("all");
  };

  return (
    <Layout title="Proposal Chat">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <FontAwesomeIcon
                  icon="comment"
                  className="mr-3 text-primary-500"
                />
                Proposal Chat
              </h1>
              <p className="text-slate-600 mt-2">
                Select a proposal to start an AI-powered conversation about your
                project
              </p>
            </div>

            {/* Stats */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">
                  {filteredProposals.length}
                </div>
                <div className="text-sm text-slate-600">Available</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">
                  {
                    filteredProposals.filter((p) => p.status === "Active")
                      .length
                  }
                </div>
                <div className="text-sm text-slate-600">Active</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <FontAwesomeIcon
                  icon="search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Search proposals by name, customer, or project..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace("_", " ")}
                  </option>
                ))}
              </select>

              {(searchTerm ||
                statusFilter !== "Active" ||
                typeFilter !== "all") && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors duration-200 flex items-center space-x-1"
                  title="Clear all filters"
                >
                  <FontAwesomeIcon icon="times" className="text-sm" />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
            </div>
          </div>

          {/* Active filters indicator */}
          {(searchTerm || statusFilter !== "Active" || typeFilter !== "all") && (
            <div className="mt-4 flex items-center space-x-2 text-sm text-slate-600">
              <FontAwesomeIcon icon="filter" className="text-xs" />
              <span>
                Showing {filteredProposals.length} of {proposals?.length || 0}{" "}
                proposals
              </span>
              {searchTerm && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                  "{searchTerm}"
                </span>
              )}
              {statusFilter !== "Active" && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs">
                  Status: {statusFilter}
                </span>
              )}
              {typeFilter !== "all" && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                  Type: {typeFilter.replace("_", " ")}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Proposals Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          {proposalsError ? (
            <ErrorMessage
              message={proposalsError}
              onRetry={() => fetchProposals()}
            />
          ) : proposalsLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Loading proposals..." />
            </div>
          ) : filteredProposals.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon
                icon="comment"
                className="text-4xl text-slate-300 mb-4"
              />
              {proposals?.length === 0 ? (
                <>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    No Proposals Available
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Create your first proposal to start AI-powered
                    conversations.
                  </p>
                  <button
                    onClick={() => (window.location.href = "/proposals/new")}
                    className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2 mx-auto"
                  >
                    <FontAwesomeIcon icon="plus" />
                    <span>Create First Proposal</span>
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    No Proposals Match Your Search
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Try adjusting your search terms or filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    Clear All Filters
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Results header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">
                  Select a Proposal to Chat About ({filteredProposals.length})
                </h2>

                {/* Sort options (future enhancement) */}
                <div className="text-sm text-slate-500">
                  Sorted by most recent
                </div>
              </div>

              {/* Proposals grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProposals.map((proposal) => (
                  <ProposalCard
                    key={proposal.proposal_id}
                    proposal={proposal}
                    compact={false}
                  />
                ))}
              </div>

              {/* Load more section (future enhancement) */}
              {filteredProposals.length >= 20 && (
                <div className="text-center mt-8 pt-6 border-t border-slate-200">
                  <p className="text-slate-600 text-sm">
                    Showing {filteredProposals.length} proposals
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProposalChatPage;
